<template>
  <v-app-bar :dark="isComplete">
    <v-container class="pa-0">
      <v-row dense elevation="6">
        <v-col cols="auto">
          <v-btn icon @click="goBack" color="secondarytext">
            <v-icon>keyboard_arrow_left</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="auto" class="my-auto">
          <div class="text-h5 font-weight-bold">
            {{ $vuetify.breakpoint.mdAndUp ? checkin.name : "" }}
            <template v-if="allowEdit">
              <v-btn color="lighttext" icon @click="editCheckin">
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn
                x-small
                color="error"
                class="subtitle-2"
                text
                @click="unstartCheckin"
                v-if="checkin.startDate != null"
              >
                cancel
              </v-btn>
              <span v-else class="darkaccent--text subtitle-2">
                Preparing
              </span>
            </template>
            <span
              v-else-if="checkin.startDate != null && !isComplete"
              class="success--text subtitle-2 ml-2"
            >
              Meeting started
            </span>
          </div>
        </v-col>
        <v-spacer></v-spacer>
        <v-col cols="auto" :class="{ 'my-auto': true, 'mr-4': true }">
          <v-btn color="lighttext" icon @click="editAttendees">
            <v-icon small>person_add</v-icon>
          </v-btn>
          <overlap-avatars
            :size="25"
            :users="checkin.attendees"
            :overlap="3"
            :dark="isComplete"
            v-on:click="editAttendees"
            :class="{ 'cursor-pointer': true }"
          ></overlap-avatars>
        </v-col>
        <v-col cols="auto" class="my-auto">
          <template v-if="!isComplete">
            <template v-if="allowEdit">
              <template v-if="checkin.startDate == null">
                <v-btn
                  color="performance"
                  class="mr-2 hidden-md-and-down"
                  @click="startCheckin"
                >
                  <v-icon class="mr-2">forum</v-icon>
                  Start Meeting
                </v-btn>
                <v-btn
                  color="performance"
                  class="hidden-lg-and-up my-auto"
                  @click="startCheckin"
                >
                  <v-icon>forum</v-icon>
                </v-btn>
              </template>
              <template v-else>
                <v-btn
                  color="error"
                  class="mr-2 hidden-md-and-down"
                  @click="completeCheckin"
                >
                  <v-icon class="mr-2">flag</v-icon>
                  End Meeting
                </v-btn>
                <v-btn
                  icon
                  color="error"
                  class="hidden-lg-and-up my-auto"
                  @click="completeCheckin"
                >
                  <v-icon>flag</v-icon>
                </v-btn>
              </template>
              <v-btn
                outlined
                color="lighttext"
                class="mr-2 hidden-md-and-down"
                @click="shareCheckin"
              >
                <v-icon class="mr-2">share</v-icon>
              </v-btn>
              <v-btn
                icon
                color="lighttext"
                class="hidden-lg-and-up my-auto"
                @click="shareCheckin"
              >
                <v-icon>share</v-icon>
              </v-btn>
            </template>
            <v-btn
              outlined
              color="secondary"
              class="my-auto"
              @click="$emit('sync')"
              :height="35"
              :width="45"
              :min-width="45"
            >
              <v-icon>refresh</v-icon>
            </v-btn>
          </template>
          <v-btn
            outlined
            color="white"
            v-else
            class="my-auto"
            :to="`/meetings/${checkin.definitionId}`"
          >
            <v-icon class="mr-2" color="white">cancel</v-icon>
            {{ checkin.completeDate | longDate }}
          </v-btn>
        </v-col>
      </v-row>
    </v-container>
    <template v-slot:extension>
      <v-container>
        <v-tabs v-model="tab" :color="tabColor">
          <v-tab :class="{ 'd-none': hideTabs }">Talking Points</v-tab>
          <v-tab
            :disabled="isComplete"
            :class="{ 'd-none': !settings.isGoalsActive || hideTabs }"
          >
            Goals
          </v-tab>
          <v-tab :class="{ 'd-none': hideTabs }">Action Items</v-tab>
          <v-tab v-if="!isComplete">History</v-tab>
        </v-tabs>
      </v-container>
    </template>
    <edit-check-in-dialog ref="editCheckin"></edit-check-in-dialog>
    <edit-check-in-attendees-dialog
      ref="editCheckinAttendees"
      :allowEdit="allowEdit"
    ></edit-check-in-attendees-dialog>
    <share-check-in-dialog
      ref="shareCheckin"
      v-on:show-invite="editAttendees"
    ></share-check-in-dialog>
  </v-app-bar>
</template>

<script>
import { mapGetters } from "vuex";
import OverlapAvatars from "@/components/common/OverlapAvatars";
import EditCheckInDialog from "@/components/checkin/EditCheckInDialog";
import EditCheckInAttendeesDialog from "@/components/checkin/EditCheckInAttendeesDialog";
import ShareCheckInDialog from "@/components/checkin/ShareCheckInDialog";
export default {
  name: "CheckInAppBar",
  components: {
    OverlapAvatars,
    EditCheckInDialog,
    EditCheckInAttendeesDialog,
    ShareCheckInDialog
  },
  props: {},
  data: () => ({}),
  computed: {
    allowEdit() {
      return this.isOwner && !this.isArchived && !this.isComplete;
    },
    isOwner() {
      return this.checkin.ownerIds.includes(this.profile.id);
    },
    tabColor() {
      switch (this.tab) {
        case 0:
          return "performance";
        case 1:
          return "success";
        case 2:
          return "info";
        default:
          return this.isComplete ? "white" : "secondary";
      }
    },
    isArchived() {
      return this.checkin.archiveDate != null;
    },
    hideTabs() {
      return this.isArchived && !this.$route.params.id;
    },
    isComplete() {
      return this.checkin.completeDate != null;
    },
    hasUrgentTalkingPoint() {
      return this.checkin.talkingPoints.filter(x => x.isUrgent).length;
    },
    talkingPointCount() {
      return this.checkin.talkingPoints.length;
    },
    actionItemCount() {
      return this._.union(
        this.checkin.pastActionItems,
        this.checkin.actionItems
      ).filter(x => x.completeDate == null).length;
    },
    tab: {
      get() {
        return this.checkin_tab;
      },
      set(val) {
        if (val == 1 && !this.settings.isGoalsActive) {
          val = 2;
        }
        this.$store.dispatch("checkin/setTab", val);
      }
    },
    ...mapGetters({
      checkin: "checkin/checkin",
      checkin_tab: "checkin/tab",
      settings: "profile/settings",
      profile: "profile/profile"
    })
  },
  methods: {
    leaveCheckIn() {
      this.$router.push({ path: "/features/meetings" });
    },
    goBack() {
      this.leaveCheckIn();
    },
    startCheckin() {
      this.tab = 0;
      this.$router.push({ path: `${this.$route.path}/start` });
    },
    unstartCheckin() {
      this.$store
        .dispatch("checkin/unstartCheckin")
        .then(() => {
          this.leaveCheckIn();
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while unstarting Check In."
          );
        });
    },
    completeCheckin() {
      this.saving = true;
      this.$store
        .dispatch("checkin/completeCheckin")
        .then(() => {
          this.leaveCheckIn();
        })
        .catch(error => {
          this.logError(error);
          this.$root.$alert(
            "Error Occurred",
            "An error occured while completing Check In."
          );
        })
        .finally(() => {
          this.saving = false;
        });
    },
    shareCheckin() {
      this.$refs.shareCheckin.showDialog(this.checkin);
    },
    editCheckin() {
      this.$refs.editCheckin.showDialog(this.checkin);
    },
    editAttendees() {
      this.$refs.editCheckinAttendees.showDialog(this.checkin);
    }
  }
};
</script>

<style lang="scss" scoped>
.theme--light ::v-deep {
  .v-toolbar__content,
  .v-toolbar__extension {
    background-color: #fff;
  }
}

.theme--dark ::v-deep {
  .v-toolbar__content,
  .v-toolbar__extension {
    background-color: var(--v-secondary-base);
  }
}
</style>
